import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError } from '../../api/envelope';
import { useAuth } from '../../auth/context';
import {
  canCallInbox,
  canReplyAnywhere,
  canReplyInbox,
  canUseCalls,
  hasNoChannelAccess,
  replyBlockedReason,
  useCapabilities,
} from '../../lib/capabilities';
import { useLoad } from '../../lib/useLoad';
import { CommsContext, type CommsState } from './context';
import { makeRealSource, type CommsSource } from './source';

/**
 * Module-level data for Communications: the data source plus the slow-moving
 * catalogs every view shares (inboxes, agents, labels, canned responses).
 * Conversation/message state lives in the per-view hooks, not here.
 */
export function CommsProvider({
  children,
  source: sourceOverride,
  identity,
}: {
  children: ReactNode;
  /** Preview/tests inject a fixture source; the app uses the real one. */
  source?: CommsSource;
  /** Embed/preview know the admin without a local Firebase session. */
  identity?: { email: string | null };
}) {
  const { tenant, email: authEmail } = useAuth();
  const { me: capabilities, reload: reloadCapabilities } = useCapabilities();
  const email = identity ? identity.email : authEmail;

  const source = useMemo<CommsSource>(
    () => sourceOverride ?? makeRealSource(tenant ?? ''),
    [sourceOverride, tenant],
  );

  const inboxesLoad = useLoad(() => source.api.listInboxes(), [source]);
  // Config-only admins get 1036 on /agents; the module still works, just
  // without assignee names. Same tolerance for labels/canned.
  const agentsLoad = useLoad(() => source.api.listAgents().catch(() => []), [source]);
  const labelsLoad = useLoad(() => source.api.listLabels().catch(() => []), [source]);
  const cannedLoad = useLoad(() => source.api.listCanned().catch(() => []), [source]);
  // Same tolerance, one step further: an older service has no endpoint for these
  // at all, and a tenant whose dispatch grant is missing gets an empty list from
  // one that does. Both mean the same thing to the pickers — no blocks.
  const blocksLoad = useLoad(() => source.api.listTemplateBlocks().catch(() => []), [source]);

  /**
   * A send came back 1036. It used to latch the whole module read-only — a
   * guess made for a grant revoked mid-session — but a refusal is per channel
   * (View on the SMS line, Write on email), and the latch greyed out every
   * thread and the Campaigns button over one. Re-reading the grants is the
   * precise version of the same guess: /meta says which channel, if any.
   */
  const noteSendDenied = useCallback(() => reloadCapabilities(), [reloadCapabilities]);

  /**
   * The module-wide Refresh. Everything here is realtime in normal operation,
   * so the button exists for the case where it was not — a socket that dropped
   * without saying so, or a change made somewhere the CRM never hears about
   * (a dispatch role edited, a mailbox re-authorised). Embedded in the
   * dashboard there is no address bar to reload from, which is the case that
   * makes it necessary rather than merely convenient.
   *
   */
  const [refreshNonce, setRefreshNonce] = useState(0);
  const reloadInboxesFn = inboxesLoad.reload;
  const reloadAgents = agentsLoad.reload;
  const reloadLabels = labelsLoad.reload;
  const reloadCanned = cannedLoad.reload;
  const reloadBlocks = blocksLoad.reload;
  const refreshAll = useCallback(() => {
    reloadInboxesFn();
    reloadAgents();
    reloadLabels();
    reloadCanned();
    reloadBlocks();
    reloadCapabilities();
    setRefreshNonce((nonce) => nonce + 1);
  }, [reloadInboxesFn, reloadAgents, reloadLabels, reloadCanned, reloadBlocks, reloadCapabilities]);

  /**
   * An admin changed a role, a membership or a contact owner — or connected or
   * removed a channel, which sends the same signal. Re-read the grants, and
   * re-read the channel list with them: a rail that keeps a removed mailbox, or
   * lacks a new one, until the page is reloaded is the same staleness one
   * screen over.
   */
  useEffect(
    () =>
      source.subscribe((event) => {
        if (event.name !== 'access.changed') return;
        reloadCapabilities();
        reloadInboxesFn();
      }),
    [source, reloadCapabilities, reloadInboxesFn],
  );

  const agents = useMemo(() => agentsLoad.data ?? [], [agentsLoad.data]);
  const me = useMemo(
    () =>
      email
        ? (agents.find((agent) => agent.email.toLowerCase() === email.toLowerCase()) ?? null)
        : null,
    [agents, email],
  );

  /**
   * The bucket this browser keeps composer drafts in. The agent's address is in
   * it because a dispatch office shares browser profiles — the next person to
   * sign in at this desk must not find someone else's unsent reply waiting in a
   * thread, ready to send under their name. Both halves are known at first
   * render (the embed is not mounted until its INIT names them), which is what
   * lets the composer restore on mount rather than after a round of loading.
   */
  const draftScope = useMemo(() => `${source.tenant}:${email ?? ''}`, [source.tenant, email]);

  const canReply = useCallback((inboxId: number) => canReplyInbox(capabilities, inboxId), [capabilities]);
  const canCall = useCallback(
    (inboxId: number) => canCallInbox(capabilities, inboxId),
    [capabilities],
  );
  const replyBlocked = useCallback(
    (inboxId: number | null) => replyBlockedReason(capabilities, inboxId),
    [capabilities],
  );
  const sendDenied = !canReplyAnywhere(capabilities);
  const noChannelAccess = hasNoChannelAccess(capabilities);

  const value = useMemo<CommsState>(
    () => ({
      source,
      inboxes: inboxesLoad.data ?? [],
      inboxesLoading: inboxesLoad.loading,
      inboxesRefreshing: inboxesLoad.refreshing,
      accessDenied:
        inboxesLoad.error instanceof ApiError && inboxesLoad.error.kind === 'forbidden',
      agents,
      labels: labelsLoad.data ?? [],
      canned: cannedLoad.data ?? [],
      templateBlocks: blocksLoad.data ?? [],
      me,
      draftScope,
      capabilities,
      canReply,
      canCall,
      replyBlocked,
      callsAvailable: canUseCalls(capabilities),
      sendDenied,
      noChannelAccess,
      noteSendDenied,
      reloadInboxes: inboxesLoad.reload,
      reloadLabels,
      refreshAll,
      refreshNonce,
      // Only the module's own reads. Each view holds its own progress bar, and
      // claiming to still be busy on their behalf would be guessing.
      refreshingAll:
        inboxesLoad.refreshing ||
        agentsLoad.refreshing ||
        labelsLoad.refreshing ||
        cannedLoad.refreshing ||
        blocksLoad.refreshing,
    }),
    [
      source,
      inboxesLoad.data,
      inboxesLoad.loading,
      inboxesLoad.refreshing,
      inboxesLoad.error,
      inboxesLoad.reload,
      agents,
      labelsLoad.data,
      cannedLoad.data,
      blocksLoad.data,
      me,
      draftScope,
      capabilities,
      canReply,
      canCall,
      replyBlocked,
      sendDenied,
      noChannelAccess,
      noteSendDenied,
      reloadLabels,
      refreshAll,
      refreshNonce,
      agentsLoad.refreshing,
      labelsLoad.refreshing,
      cannedLoad.refreshing,
      blocksLoad.refreshing,
    ],
  );

  return <CommsContext.Provider value={value}>{children}</CommsContext.Provider>;
}
